import { useState } from "react";
import { api } from "../../lib/api";
import { X, Upload, CheckCircle2, AlertCircle, FileSpreadsheet } from "lucide-react";

/**
 * BulkStatusUpdateModal — planilha simples (codigo + status) para atualização
 * em massa de status. Reaproveita o parser Excel/CSV do backend (mesmas
 * funções openpyxl/csv já usadas pelo Cadastro Rápido).
 *
 * Fluxo em três etapas:
 *   1. upload    — usuário seleciona arquivo, clica "Analisar planilha"
 *   2. preview   — mostra contagem por status alvo + códigos não encontrados
 *   3. result    — após confirmar, exibe relatório final
 */
const STATUS_LABELS = {
  available: "Disponível",
  paused: "Pausada",
  sold: "Vendida",
};

const STATUS_COLOR = {
  available: "text-[#3E6A1E]",
  paused: "text-[#8C7B5E]",
  sold: "text-[#B85050]",
};

export default function BulkStatusUpdateModal({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [stage, setStage] = useState("upload"); // upload | preview | result
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setStage("upload");
    setPreview(null);
    setResult(null);
    setSubmitting(false);
    setError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const analyse = async () => {
    if (!file) {
      setError("Selecione uma planilha.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "true");
      const r = await api.post("/admin/products/bulk-status-update", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setStage("preview");
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível analisar a planilha.");
    } finally {
      setSubmitting(false);
    }
  };

  const confirm = async () => {
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "false");
      const r = await api.post("/admin/products/bulk-status-update", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      setStage("result");
      if (onDone) onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha ao aplicar atualização.");
    } finally {
      setSubmitting(false);
    }
  };

  const summaryLine = (data) => {
    if (!data?.by_status) return "";
    const parts = [];
    if (data.by_status.available) parts.push(`${data.by_status.available} → Disponível`);
    if (data.by_status.paused) parts.push(`${data.by_status.paused} → Pausada`);
    if (data.by_status.sold) parts.push(`${data.by_status.sold} → Vendida`);
    return parts.join(" · ");
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="bulk-status-modal"
    >
      <div
        className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-[#A8A8A8] mb-1">Catálogo</p>
            <h2 className="font-display text-3xl">Atualização Rápida de Status</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-[#6B6B6B] hover:text-[#1A1A1A]"
            data-testid="bulk-status-close"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Stage 1 — upload */}
        {stage === "upload" && (
          <div data-testid="bulk-status-upload-stage">
            <p className="text-sm text-[#6B6B6B] leading-relaxed mb-6">
              Envie uma planilha (<strong>.xlsx</strong> ou <strong>.csv</strong>) com exatamente
              duas colunas:
              {" "}<code className="font-mono text-[#1A1A1A]">codigo</code>
              {" "}e <code className="font-mono text-[#1A1A1A]">status</code>.
              Valores aceitos para status: <em>Disponível</em>, <em>Pausada</em> ou <em>Vendida</em>
              {" "}(sem distinção de acento ou maiúscula/minúscula).
            </p>

            <div className="bg-[#FAF9F7] border border-[#E8E4DF] p-6 mb-6">
              <p className="overline text-[#A8A8A8] mb-3 inline-flex items-center gap-2">
                <FileSpreadsheet size={14} /> Planilha
              </p>
              <input
                type="file"
                accept=".xlsx,.csv"
                disabled={submitting}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm"
                data-testid="bulk-status-file-input"
              />
              {file && (
                <p className="text-xs text-[#6B6B6B] mt-2">
                  {file.name} ({(file.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>

            {error && (
              <p className="text-sm text-[#B85050] mb-4" data-testid="bulk-status-error">
                {error}
              </p>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={analyse}
                disabled={submitting || !file}
                className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-status-analyse-btn"
              >
                <Upload size={14} className="mr-2" />
                {submitting ? "Analisando…" : "Analisar planilha"}
              </button>
              <button type="button" onClick={handleClose} className="btn-secondary">
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Stage 2 — preview */}
        {stage === "preview" && preview && (
          <div data-testid="bulk-status-preview-stage">
            <p className="text-sm text-[#6B6B6B] mb-4">
              Análise concluída. Confira o resumo antes de confirmar a atualização.
            </p>

            <div className="bg-[#F5EFE8] border border-[#B8A88A] p-5 mb-4">
              <p className="overline text-[#8C7B5E] mb-2">Resumo por status</p>
              <p className="font-display text-2xl" data-testid="bulk-status-preview-summary">
                {summaryLine(preview) || "Nenhuma atualização válida"}
              </p>
              <p className="text-xs text-[#6B6B6B] mt-2">
                {preview.updated_count} peças serão atualizadas
                {preview.unchanged_count > 0
                  ? ` · ${preview.unchanged_count} já estão no status informado`
                  : ""}
              </p>
            </div>

            {preview.not_found_count > 0 && (
              <div className="bg-[#FFF4EF] border border-[#F0C8B8] p-4 mb-4">
                <p className="text-sm font-medium text-[#B85050] mb-2 inline-flex items-center gap-2">
                  <AlertCircle size={14} /> {preview.not_found_count} código(s) não encontrado(s)
                </p>
                <div className="max-h-24 overflow-y-auto text-xs font-mono text-[#6B6B6B]" data-testid="bulk-status-notfound">
                  {preview.not_found.join(", ")}
                </div>
              </div>
            )}

            {preview.invalid_count > 0 && (
              <div className="bg-[#FFF4EF] border border-[#F0C8B8] p-4 mb-4">
                <p className="text-sm font-medium text-[#B85050] mb-2 inline-flex items-center gap-2">
                  <AlertCircle size={14} /> {preview.invalid_count} linha(s) com status inválido
                </p>
                <div className="max-h-24 overflow-y-auto text-xs font-mono text-[#6B6B6B]">
                  {preview.invalid.map((i, idx) => (
                    <div key={idx}>{i.codigo} → &quot;{i.status}&quot;</div>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <p className="text-sm text-[#B85050] mb-4" data-testid="bulk-status-error">
                {error}
              </p>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={confirm}
                disabled={submitting || preview.updated_count === 0}
                className="btn-primary inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-status-confirm-btn"
              >
                {submitting ? "Aplicando…" : `Confirmar atualização (${preview.updated_count})`}
              </button>
              <button type="button" onClick={reset} className="btn-secondary" data-testid="bulk-status-back-btn">
                Trocar arquivo
              </button>
            </div>
          </div>
        )}

        {/* Stage 3 — result */}
        {stage === "result" && result && (
          <div data-testid="bulk-status-result-stage">
            <div className="bg-[#F0F5EA] border border-[#3E6A1E] p-5 mb-4">
              <p className="inline-flex items-center gap-2 text-[#3E6A1E] mb-2">
                <CheckCircle2 size={16} />
                <span className="font-medium">Atualização concluída</span>
              </p>
              <p className="font-display text-2xl" data-testid="bulk-status-result-count">
                {result.updated_count} peça(s) atualizada(s)
              </p>
              {summaryLine(result) && (
                <p className="text-xs text-[#6B6B6B] mt-2">{summaryLine(result)}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4 text-center">
              <div className="bg-[#FAF9F7] border border-[#E8E4DF] p-4">
                <p className="overline text-[#A8A8A8] mb-1">Atualizadas</p>
                <p className="font-display text-2xl text-[#3E6A1E]">{result.updated_count}</p>
              </div>
              <div className="bg-[#FAF9F7] border border-[#E8E4DF] p-4">
                <p className="overline text-[#A8A8A8] mb-1">Sem alteração</p>
                <p className="font-display text-2xl text-[#6B6B6B]">{result.unchanged_count}</p>
              </div>
              <div className="bg-[#FAF9F7] border border-[#E8E4DF] p-4">
                <p className="overline text-[#A8A8A8] mb-1">Não encontrados</p>
                <p className="font-display text-2xl text-[#B85050]">{result.not_found_count}</p>
              </div>
            </div>

            {result.not_found_count > 0 && (
              <div className="mb-4">
                <p className="overline text-[#A8A8A8] mb-2">Códigos não encontrados</p>
                <div className="max-h-32 overflow-y-auto text-xs font-mono text-[#6B6B6B] bg-[#FAF9F7] p-3 border border-[#E8E4DF]">
                  {result.not_found.join(", ")}
                </div>
              </div>
            )}

            {result.invalid_count > 0 && (
              <div className="mb-4">
                <p className="overline text-[#A8A8A8] mb-2">Status inválidos (ignorados)</p>
                <div className="max-h-32 overflow-y-auto text-xs font-mono text-[#6B6B6B] bg-[#FAF9F7] p-3 border border-[#E8E4DF]">
                  {result.invalid.map((i, idx) => (
                    <div key={idx}>{i.codigo} → &quot;{i.status}&quot;</div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mt-6">
              <button
                type="button"
                onClick={handleClose}
                className="btn-primary"
                data-testid="bulk-status-close-btn"
              >
                Fechar
              </button>
              <button type="button" onClick={reset} className="btn-secondary">
                Nova atualização
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
